<script setup lang="ts">
/**
 * /how-to-cancel/<slug> — how to cancel one competitor's subscription, step by
 * step (constants/cancelGuides.ts). Targets "cancel <name> subscription" and
 * "how to cancel <name>", which the /alternatives/<slug> page answers in one
 * card and does not rank for. English only, like those searches.
 */
import { findCompetitor, withOurPrices } from '~/constants/competitors'
import { findCancelGuide } from '~/constants/cancelGuides'

false && /*#__PURE__*/ defineI18nRoute(false)

definePageMeta({
  layout: false,
  auth: false,
  englishOnly: true,
})

const { t } = useI18n()
const route = useRoute()
const { faqPageNode } = useJsonLd()

const slug = String(route.params.slug ?? '')
const guide = findCancelGuide(slug)
const competitor = findCompetitor(slug)
if (!guide || !competitor) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const path = `/how-to-cancel/${guide.slug}`
const name = competitor.name

/** The alternatives page's last question is the one about a subscription-free option. */
const noSubscriptionAnswer = withOurPrices(competitor.faq[competitor.faq.length - 1]!.a)

const faqItems = [
  { id: 'how', question: `How do I cancel ${name}?`, answer: guide.short },
  { id: 'when', question: `When should I cancel the ${name} trial?`, answer: guide.timing.text },
  ...(guide.refund ? [{ id: 'refund', question: `Can I get a refund from ${name}?`, answer: guide.refund.text }] : []),
  { id: 'after', question: `What happens to my resume after I cancel ${name}?`, answer: guide.after.text },
  { id: 'alternative', question: 'Is there a resume builder without a subscription?', answer: noSubscriptionAnswer },
]

const extraNodes = [faqPageNode(path, faqItems)]
</script>

<template>
  <MarketingFeaturePage
    :seo-title="guide.seoTitle"
    :seo-description="guide.seoDescription"
    :path="path"
    :breadcrumb-title="`How to cancel ${name}`"
    :parent="{ name: t('alternatives.hub.heading'), path: '/alternatives' }"
    :extra-nodes="extraNodes"
  >
    <MarketingCancelGuide :guide="guide" :competitor="competitor" />

    <MarketingFaqSection
      :title="`More questions about cancelling ${name}`"
      :items="faqItems"
      :name="`cancel-${guide.slug}-faq`"
    />
  </MarketingFeaturePage>
</template>
